// Where the storefront keeps a merchant's pass and what the bar does with it, kept free of React so it is
// tested alone. The pass lives in this store's own browser storage: it arrives once in the address the admin
// opened, is taken out of it at once, and is kept until it expires or the merchant exits.
import { ThemePageTypeEnum } from '@xshop/shared';

/** A pass the store vouched for, as the bar needs it. */
export interface HeldPass {
  token: string;
  expiresAt: string;
  adminUrl: string;
  theme: { handle: string; name: string };
}

/** The query the admin's View store link carries the pass in. */
export const PASS_PARAM = 'xshop_pass';

// Names where one store's pass is kept, so two stores on one browser never share a bar.
const passKey = (store: string): string => `xshop:merchant-pass:${store}`;

// Names where one store's "Hide bar" is kept for the visit.
const hiddenKey = (store: string): string => `xshop:merchant-bar-hidden:${store}`;

// Takes the pass out of an address, handing back the token and the address without it.
export function takePass(href: string): { token: string | null; cleaned: string } {
  const url = new URL(href);
  const token = url.searchParams.get(PASS_PARAM);
  url.searchParams.delete(PASS_PARAM);
  return { token, cleaned: `${url.pathname}${url.search}${url.hash}` };
}

// Reads a kept pass back, dropping one that has expired or does not read as a pass at all.
export function readHeld(raw: string | null, now = Date.now()): HeldPass | null {
  try {
    const held = JSON.parse(raw ?? 'null') as HeldPass | null;
    if (!held || typeof held.token !== 'string' || !held.theme?.handle) return null;
    return new Date(held.expiresAt).getTime() > now ? held : null;
  } catch {
    return null;
  }
}

// Builds the link the bar's Customize opens: the theme in the editor, on the page the merchant is looking at.
export function editThemeUrl(held: HeldPass, page: string): string {
  const editable = ThemePageTypeEnum.safeParse(page).success ? page : 'home';
  return `${held.adminUrl}/online-store/themes/${encodeURIComponent(held.theme.handle)}/editor?page=${editable}`;
}

// Runs one storage call, saying nothing when the browser keeps no storage; the bar then lasts one page.
function stored<T>(run: () => T, fallback: T): T {
  try { return run(); } catch { return fallback; }
}

// Keeps, reads and forgets one store's pass and whether its bar is hidden for this visit.
export const passStore = (store: string) => ({
  load: (): HeldPass | null => readHeld(stored(() => localStorage.getItem(passKey(store)), null)),
  save: (held: HeldPass): void => stored(() => localStorage.setItem(passKey(store), JSON.stringify(held)), undefined),
  drop: (): void => stored(() => localStorage.removeItem(passKey(store)), undefined),
  hidden: (): boolean => stored(() => sessionStorage.getItem(hiddenKey(store)) === '1', false),
  hide: (): void => stored(() => sessionStorage.setItem(hiddenKey(store), '1'), undefined)
});
